/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import { styled } from '@mui/material/styles';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import TableHead from '@mui/material/TableHead';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import MDDatePicker from 'components/MDDatePicker';
import MDDateRange from 'components/MDDateRange';
import MDTextarea from 'components/MDTextarea';
import TabPanel from 'components/TabPanel/TabPanel';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import PurchasePayStatus from './PurchasePayStatus';
import PurchasePayList from './PurchasePayList';
import PurchasePaySlip from './PurchasePaySlip';

const initAttr = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  account_id: '',
  po_id: '0',
  po_supplier: '',
  po_total: '',
  po_vat: '',
  po_nettotal: '',
  po_paid: '',
  po_balance: '',
  po_type: '',
  po_user: '',
  po_code: '',
  po_remark: '',
  po_date: '',
  invoice_code: '',
  total: '',
  vat: '',
  nettotal: '',
  total_pay: '',
  vat_pay: '',
  nettotal_pay: '',
  vat_type: 'NO',
  status: 'edit',
  payment_type: 'cash',
  slip_id: '',
  slip: '',
  print_url: '',
  created_at: '',
  updated_at: '',
  date_expire: Func.dateNow(),
  invoice_date: Func.dateNow(),
};

const initFind = {
  search: '',
  order_by: 'tb.code',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  status: 'ALL',
  period: 'ALL',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const stepperName = {
  edit: {
    name: 'แก้ไขข้อมูล',
    color: 'warning',
    icon: 'border_color',
  },
  waiting: {
    name: 'รอการชำระ',
    color: 'info',
    icon: 'task_alt',
  },
  success: {
    name: 'ชำระเงินแล้ว',
    color: 'info',
    icon: 'task_alt',
  },
  reject: {
    name: 'ยกเลิก',
    color: 'error',
    icon: 'cancel',
  },
};

const cboStatus = [
  { id: 'ALL', name: 'ทั้งหมด' },
  { id: 'edit', name: stepperName.edit.name },
  { id: 'waiting', name: stepperName.waiting.name },
  { id: 'success', name: stepperName.success.name },
  { id: 'reject', name: stepperName.reject.name },
];

const cboPaymentType = [
  { id: 'cash', name: 'เงินสด' },
  { id: 'transfer', name: 'โอนเงิน' },
];

const initStepper = {};

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  // borderRadius: `5px`,
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, .05)' : 'rgba(0, 0, 0, 0.02)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  // background: "#f5f5f5",
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function PurchasePay() {
  const mod = 'purchase_pay';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalDel, setModalDel] = useState(false);
  const [stepper, setStepper] = useState(initStepper);
  const [cboPO, setCboPO] = useState([]);
  const [cboAccount, setCboAccount] = useState([]);
  const [list, setList] = useState([]);
  const [tab, setTab] = useState(0);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.code', label: t(`attr.code`), align: 'center' },
    { id: 'tb.po_id', label: 'PO', align: 'center' },
    { id: 'tb.type_id', label: 'ประเภท', align: 'left' },
    { id: 'tb.supplier_id', label: 'ผู้จัดจำหน่าย', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้สั่งซื้อ', align: 'left' },
    { id: 'tb.remark', label: 'หมายเหตุ', align: 'left' },
    { id: 'tb.invoice_date', label: 'วันสั่งซื้อ', align: 'center' },
    { id: 'tb.created_at', label: 'วันที่สร้าง', align: 'center' },
    { id: 'print', label: '', align: 'center', sort: false },
  ];

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      status: data.status,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(result.data);
        setNavigate(result.navigate);
        setStepper(result.stepper);
        setList(result.list);

        setLoading(false);
      }
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.po_id, 1)) return;
    if (!Func.verifyLength(attr.invoice_code, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      po_id: attr.po_id,
      remark: attr.remark,
      invoice_code: attr.invoice_code,
      invoice_date: attr.invoice_date,
      date_expire: attr.date_expire,
      total: attr.total,
      vat: attr.vat,
      nettotal: attr.nettotal,
      total_pay: attr.total,
      vat_pay: attr.vat,
      nettotal_pay: attr.nettotal,
      payment_type: attr.payment_type,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.invoice_code, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setLoading(true);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      remark: attr.remark,
      invoice_code: attr.invoice_code,
      invoice_date: attr.invoice_date,
      date_expire: attr.date_expire,
      total: attr.total,
      vat: attr.vat,
      nettotal: attr.nettotal,
      total_pay: attr.total,
      vat_pay: attr.vat,
      nettotal_pay: attr.nettotal,
      payment_type: attr.payment_type,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        open();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.${result.msg}`), 'error');
      }
    });
  };

  const approve = (status) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/approve`, app.token, {
          id: id,
          status: status,
          payment_type: attr.payment_type,
          total_pay: attr.total_pay,
          vat_pay: attr.vat_pay,
          nettotal_pay: attr.nettotal_pay,
          account_id: attr.account_id,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            window.location.reload();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const upload = (filedata, file) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/upload`, app.token, {
          id: attr.id,
          filedata: filedata,
          filename: file ? file.name : '',
          filesize: file ? file.size : 0,
          filetype: file ? file.type : '',
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setAttr({ ...attr, slip: result.image });
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const print = () => {
    window.open(attr.print_url, '_blank');
  };

  const del = () => {
    setLoading(true);
    FetchData('DELETE', `/${mod}/${attr.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view(find);
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboPO(result.po);
        setCboAccount(result.account);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const btnOpen = (item) => {
    return (
      <NavLink to={`/${mod}/${item.id}`}>
        <MDButton
          variant='text'
          color='info'
          size='small'
          sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}
        >
          {item.code}
        </MDButton>
      </NavLink>
    );
  };

  const btnPrint = (item) => {
    if (item.status === 'reject') return null;

    return (
      <MDButton
        variant='text'
        color='info'
        size='small'
        iconOnly
        onClick={() => window.open(item.print_url, '_blank')}
      >
        <Icon>print</Icon> {t(`element.print`)}
      </MDButton>
    );
  };

  const statusDisplay = (item) => {
    return (
      <Alert
        icon={<Icon>{stepperName[item.status].icon}</Icon>}
        severity={stepperName[item.status].color}
        sx={{
          fontSize: '14px',
          padding: '0 10px',
          fontWeight: 400,
          borderRadius: 5,
          whiteSpace: 'nowrap',
        }}
      >
        {stepperName[item.status].name}
      </Alert>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
            >
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              onClick={() => {
                setAttr(initAttr);
                setConfirm(false);
                setModalAdd(true);
              }}
            >
              <Icon>post_add</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={160}>
                        {statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{btnOpen(row)}</TableCell>
                      <TableCell align='center'>{row.pr_code}</TableCell>
                      <TableCell align='left'>{row.type_name}</TableCell>
                      <TableCell align='left'>{row.supplier_name}</TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='left'>{row.remark}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.invoice_date)}</TableCell>
                      <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                      <TableCell align='center'>{btnPrint(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    const readOnly = attr.status !== 'edit';

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label='เลขที่ใบแจ้งหนี้'
            onChange={(e) => setAttr({ ...attr, invoice_code: e.target.value })}
            value={attr.invoice_code}
            variant='standard'
            inputProps={{
              maxLength: 50,
              autoComplete: 'off',
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, invoice_date: Func.dateFormat(date) });
            }}
            value={new Date(attr.invoice_date)}
            input={{
              label: 'วันที่ใบแจ้งหนี้',
              variant: 'standard',
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_expire: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_expire)}
            input={{
              label: 'วันกำหนดชำระ',
              variant: 'standard',
              fullWidth: true,
            }}
            readOnly={readOnly}
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label='วิธีการชำระเงิน'
            labelDefault={t(`element.please_select`)}
            data={cboPaymentType}
            value={attr.payment_type}
            onChange={(e) => setAttr({ ...attr, payment_type: e.target.value })}
            inputProps={{
              autoComplete: 'off',
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='จำนวนชำระ'
            onChange={(e) =>
              setAttr({
                ...attr,
                total: e.target.value,
                nettotal: e.target.value * 1 + attr.vat * 1,
              })
            }
            onClick={(e) => e.target.select()}
            value={attr.total}
            variant='standard'
            type='number'
            inputProps={{
              maxLength: 20,
              className: 'text-right',
              autoComplete: 'off',
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='Vat'
            value={attr.vat}
            variant='standard'
            type='number'
            fullWidth
            onChange={(e) =>
              setAttr({
                ...attr,
                vat: e.target.value,
                nettotal: e.target.value * 1 + attr.total * 1,
              })
            }
            onClick={(e) => e.target.select()}
            inputProps={{
              maxLength: 20,
              className: 'text-right',
              autoComplete: 'off',
              readOnly: readOnly,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='รวมชำระ'
            value={attr.nettotal}
            variant='standard'
            type='number'
            fullWidth
            inputProps={{
              disabled: true,
              className: 'text-right',
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <MDTextarea
            label={`${t(`attr.remark`)} *`}
            onChange={(e) => setAttr({ ...attr, remark: e.target.value })}
            value={attr.remark}
            variant='standard'
            inputProps={{
              maxLength: 200,
              autoComplete: 'off',
              readOnly: readOnly,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderSummary = () => {
    return (
      <MDBox mb={2} className='card'>
        <MDBox p={2}>
          <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
        </MDBox>

        <TableContainer sx={{ boxShadow: 0, border: '1px solid #f2f2f2' }}>
          <Table aria-label='po table'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>No.</TableCell>
                <TableCell align='left'>สินค้า</TableCell>
                <TableCell align='right'>ราคา</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align='center' width='100'>
                    {row.no}
                  </TableCell>
                  <TableCell align='left'>{row.product_name}</TableCell>
                  <TableCell align='right' width='150'>
                    {Func.numberDisplay(row.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Table aria-label='summary table'>
            <TableBody>
              {attr.vat_type === 'NO' ? null : (
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
                  </TableCell>
                  <TableCell align='right' width='200'>
                    <MDTypography variant='h5'>{Func.numberDisplay(attr.po_total)}</MDTypography>
                  </TableCell>
                </TableRow>
              )}
              {attr.vat_type === 'NO' ? null : (
                <TableRow>
                  <TableCell align='right'>
                    <MDTypography variant='h5'>Vat 7%</MDTypography>
                  </TableCell>
                  <TableCell align='right'>
                    <MDTypography variant='h5'>{Func.numberDisplay(attr.po_vat)}</MDTypography>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
                </TableCell>
                <TableCell align='right' width='200'>
                  <MDTypography variant='h5'>{Func.numberDisplay(attr.po_nettotal)}</MDTypography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h5'>ชำระแล้ว</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h5'>{Func.numberDisplay(attr.po_paid)}</MDTypography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h5'>คงเหลือ</MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h5'>{Func.numberDisplay(attr.po_balance)}</MDTypography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderPayment = () => {
    const readOnly = attr.status !== 'waiting';

    return (
      <MDBox>
        <Accordion
          expanded={attr.payment_type === 'cash'}
          onChange={() => {
            if (attr.status === 'waiting') {
              setAttr({ ...attr, payment_type: 'cash' });
            }
          }}
        >
          <AccordionSummary aria-controls='panel1d-content' id='panel1d-header'>
            <Typography>ชำระเงินสด</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDInput
                  label='จำนวนการจ่าย'
                  onChange={(e) =>
                    setAttr({
                      ...attr,
                      total_pay: e.target.value,
                      nettotal_pay: e.target.value * 1 + attr.vat_pay * 1,
                    })
                  }
                  onClick={(e) => e.target.select()}
                  value={attr.total_pay}
                  variant='standard'
                  type='number'
                  inputProps={{
                    maxLength: 20,
                    autoComplete: 'off',
                    className: 'text-right',
                    readOnly: readOnly,
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='Vat'
                  value={attr.vat_pay}
                  variant='standard'
                  type='number'
                  fullWidth
                  onChange={(e) =>
                    setAttr({
                      ...attr,
                      vat_pay: e.target.value,
                      nettotal_pay: e.target.value * 1 + attr.total_pay * 1,
                    })
                  }
                  onClick={(e) => e.target.select()}
                  inputProps={{
                    maxLength: 20,
                    autoComplete: 'off',
                    className: 'text-right',
                    readOnly: readOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='รวมการจ่าย'
                  value={attr.nettotal_pay}
                  variant='standard'
                  type='number'
                  fullWidth
                  inputProps={{
                    disabled: true,
                    className: 'text-right',
                  }}
                />
              </Grid>
            </Grid>
            {attr.status === 'waiting' ? (
              <Grid container mt={3}>
                <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
                  <MDButton variant='outlined' color='info' onClick={() => approve('success')}>
                    <Icon>check</Icon>&nbsp;ชำระเงิน
                  </MDButton>
                </Grid>
              </Grid>
            ) : null}
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={attr.payment_type === 'transfer'}
          onChange={() => {
            if (attr.status === 'waiting') {
              setAttr({ ...attr, payment_type: 'transfer' });
            }
          }}
        >
          <AccordionSummary aria-controls='panel2d-content' id='panel2d-header'>
            <Typography>โอนเงินผ่านธนาคาร</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <MDSelect
                  label='บัญชีโอนเงิน *'
                  labelDefault={t(`element.please_select`)}
                  data={cboAccount}
                  value={attr.account_id}
                  onChange={(e) => setAttr({ ...attr, account_id: e.target.value })}
                  inputProps={{
                    autoComplete: 'off',
                    readOnly: readOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='จำนวนเงิน'
                  onChange={(e) =>
                    setAttr({
                      ...attr,
                      total_pay: e.target.value,
                      nettotal_pay: e.target.value * 1 + attr.vat_pay * 1,
                    })
                  }
                  onClick={(e) => e.target.select()}
                  value={attr.total_pay}
                  variant='standard'
                  type='number'
                  inputProps={{
                    maxLength: 20,
                    autoComplete: 'off',
                    className: 'text-right',
                    readOnly: readOnly,
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='Vat'
                  value={attr.vat_pay}
                  variant='standard'
                  type='number'
                  fullWidth
                  onChange={(e) =>
                    setAttr({
                      ...attr,
                      vat_pay: e.target.value,
                      nettotal_pay: e.target.value * 1 + attr.total_pay * 1,
                    })
                  }
                  onClick={(e) => e.target.select()}
                  inputProps={{
                    maxLength: 20,
                    className: 'text-right',
                    readOnly: readOnly,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='จำนวนการโอน'
                  value={attr.nettotal_pay}
                  variant='standard'
                  type='number'
                  fullWidth
                  inputProps={{
                    disabled: true,
                    className: 'text-right',
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper variant='outlined' sx={{ p: 2 }}>
                  <MDTypography variant='h5' mb={2}>
                    Slip
                  </MDTypography>
                  {attr.slip === '' ? (
                    <MDBox
                      component='img'
                      src='/images/nopic.jpg'
                      alt={attr.code}
                      width='100%'
                      height='100%'
                    />
                  ) : (
                    <MDBox
                      component='img'
                      src={attr.slip}
                      alt={attr.code}
                      width='100%'
                      height='100%'
                    />
                  )}
                  {attr.status === 'waiting' ? (
                    <MDBox mr={1} display='flex' justifyContent='center'>
                      <PurchasePaySlip uploadData={upload} />
                    </MDBox>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>
            {attr.status === 'waiting' ? (
              <Grid container mt={3}>
                <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
                  <MDButton variant='outlined' color='info' onClick={() => approve('success')}>
                    <Icon>check</Icon>&nbsp;ชำระเงิน
                  </MDButton>
                </Grid>
              </Grid>
            ) : null}
          </AccordionDetails>
        </Accordion>
      </MDBox>
    );
  };

  const renderPO = (readOnly = false) => {
    const pushValue = (value) => {
      cboPO.forEach((item, i) => {
        if (item.id === value) {
          setAttr({
            ...attr,
            po_id: value,
            po_type: item.type_name,
            po_date: Func.dateDisplay(item.date_record),
            po_remark: item.remark,
            po_user: item.user_name,
            po_supplier: item.supplier_name,
          });
        }
      });
    };

    return (
      <Grid container spacing={2}>
        {readOnly ? null : (
          <Grid item xs={12}>
            <MDSelect
              label='ใบสั่งซื้อ'
              labelDefault={t(`element.please_select`)}
              data={cboPO}
              value={attr.po_id}
              onChange={(e) => pushValue(e.target.value)}
              inputProps={{
                autoComplete: 'off',
                readOnly: readOnly,
              }}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <MDInput
            label='ผู้จัดจำหน่าย'
            value={attr.po_supplier}
            variant='standard'
            inputProps={{
              disabled: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ประเภทสั่งซื้อ'
            value={attr.po_type}
            variant='standard'
            inputProps={{
              disabled: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='ผู้สั่งซื้อ'
            value={attr.po_user}
            variant='standard'
            inputProps={{
              disabled: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='วันที่ใช้งาน'
            value={attr.po_date}
            variant='standard'
            inputProps={{
              disabled: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label='หมายเหตุใบสั่งซื้อ'
            value={attr.po_remark}
            variant='standard'
            multiline
            inputProps={{
              disabled: true,
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {renderPO()}
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderInfo()}
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderDelete = () => {
    return (
      <Dialog
        open={modalDel}
        onClose={() => setModalDel(false)}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-delete'
      >
        <DialogTitle>
          {t(`element.delete`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalDel(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText p={5}>{t(`alert.confirm_delete_title`)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setModalDel(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='gradient' color='error' onClick={del}>
                  <Icon>delete</Icon>&nbsp;{t(`element.deleteconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='error' onClick={() => setConfirm(true)}>
              <Icon>delete</Icon>&nbsp;{t(`element.delete`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderStepper = () => {
    const action = (key) => {
      switch (attr.status) {
        case 'edit':
          return key === 'waiting';
        case 'waiting':
          return key === 'success';
        default:
          return false;
      }
    };

    const stepLevel = (key) => {
      return (
        <Step sx={{ minWidth: 150 }}>
          <StepLabel StepIconComponent={PurchasePayStatus}>
            {action(key) ? (
              <MDButton variant='gradient' color='info' size='small' onClick={() => approve(key)}>
                <Icon>task_alt</Icon> &nbsp; {stepperName[key].name}
              </MDButton>
            ) : stepper[key] === undefined ? (
              <MDTypography variant='subtitle2'>{stepperName[key].name}</MDTypography>
            ) : attr.status === key ? (
              <MDBox>
                <MDBadge
                  badgeContent={stepperName[key].name}
                  variant='contained'
                  color={stepperName[key].color}
                  size='md'
                  container
                />
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <MDTypography variant='h6'>{stepperName[key].name}</MDTypography>
                <MDTypography variant='subtitle2'>{stepper[key].emp_name}</MDTypography>
                <MDTypography variant='subtitle2'>
                  {Func.dateTimeDisplay(stepper[key].created_at)}
                </MDTypography>
              </MDBox>
            )}
          </StepLabel>
        </Step>
      );
    };

    const stepInt = () => {
      if (attr.status === 'waiting') return 2;
      if (attr.status === 'success') return 3;
      if (attr.status === 'reject') return 9;

      return 0;
    };

    return loading ? null : (
      <MDBox className='stepper' mt={3} mb={1}>
        <Stepper activeStep={stepInt()} alternativeLabel>
          {stepLevel('edit')}
          {stepLevel('waiting')}
          {stepLevel('success')}
          {stepLevel('reject')}
        </Stepper>
      </MDBox>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          #{attr.code}
        </MDTypography>

        {renderInfo()}

        {confirm ? (
          <Grid container mt={3}>
            <Grid item xs={6}>
              <MDButton variant='outlined' color='error' onClick={() => setConfirm(false)}>
                <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='gradient' color='success' onClick={edit}>
                <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : attr.status === 'edit' ? (
          <Grid container mt={3}>
            <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
              <MDButton variant='outlined' color='success' onClick={editConfirm}>
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : null}
      </Paper>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={6}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12}>
            {renderStepper()}
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs
                    value={tab}
                    onChange={(e, v) => setTab(v)}
                    aria-label='tabs-addedit'
                    sx={{ overflowX: 'scroll' }}
                  >
                    <Tab label='ชำระเงิน' />
                    <Tab label='ใบสั่งซื้อ' />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container mb={3} spacing={2}>
                    <Grid item xs={12} lg={3}>
                      {renderInfoEdit()}
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      {renderPayment()}
                    </Grid>
                    <Grid item xs={12} lg={5}>
                      {renderSummary()}
                    </Grid>
                    {attr.status === 'reject' ? null : (
                      <Grid item xs={12} display='flex' justifyContent='center' gap={1} mt={2}>
                        <MDButton
                          variant='gradient'
                          color='error'
                          onClick={() => {
                            approve('reject');
                          }}
                        >
                          <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                        </MDButton>
                        <MDButton variant='gradient' color='info' onClick={print}>
                          <Icon>print</Icon>&nbsp;{t(`element.print`)}
                        </MDButton>
                      </Grid>
                    )}
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container mb={3} spacing={2}>
                    <Grid item xs={12} lg={3}>
                      <MDBox className='card' p={2}>
                        <MDTypography variant='h5' mb={3}>
                          #{attr.po_code}
                        </MDTypography>
                        {renderPO()}
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} lg={9}>
                      <PurchasePayList />
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.account`), t(`menu.purchase_pay`)]} />
      {renderPage()}
      {renderAdd()}
      {renderDelete()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default PurchasePay;
